import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Search, X, Upload, Trash2, ToggleLeft, ToggleRight,
  Anchor, Truck as TruckIcon, Mail, FileSpreadsheet, MapPin, Train, Globe
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  OTR: 'bg-blue-100 text-blue-700',
  DRAYAGE: 'bg-orange-100 text-orange-700',
};
const REGION_ICONS = { STATE: MapPin, PORT: Anchor, RAIL: Train };

export const CarriersTab = ({ isAdmin }) => {
  const [carriers, setCarriers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: 'all', region_id: '', search: '', is_active: 'true' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState([]);
  const [showUnsub, setShowUnsub] = useState(false);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const fetchRegions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/regions`);
      setRegions(res.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load regions:', e);
    }
  }, []);

  const fetchCarriers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.type !== 'all') params.append('carrier_type', filter.type);
      if (filter.region_id) params.append('region_id', filter.region_id);
      if (filter.search) params.append('search', filter.search);
      if (filter.is_active !== 'all') params.append('is_active', filter.is_active);
      const res = await axios.get(`${API}/carriers?${params.toString()}`);
      setCarriers(res.data.carriers);
      setTotal(res.data.total);
      setSelected(new Set());
    } catch (e) {
      toast.error('Failed to load carriers');
    } finally { setLoading(false); }
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/carriers/stats`);
      setStats(res.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load carrier stats:', e);
    }
  }, []);

  useEffect(() => { fetchRegions(); fetchStats(); }, [fetchRegions, fetchStats]);
  useEffect(() => { fetchCarriers(); }, [fetchCarriers]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this carrier?')) return;
    try {
      await axios.delete(`${API}/carriers/${id}`);
      toast.success('Carrier deleted');
      fetchCarriers(); fetchStats();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleToggle = async (carrier) => {
    try {
      await axios.put(`${API}/carriers/${carrier.id}`, { is_active: !carrier.is_active });
      toast.success(carrier.is_active ? 'Carrier deactivated' : 'Carrier activated');
      fetchCarriers(); fetchStats();
    } catch (e) { toast.error('Failed to update'); }
  };

  const loadUnsubscribed = async () => {
    try {
      const res = await axios.get(`${API}/carriers/unsubscribed`);
      setUnsubscribed(res.data);
      setShowUnsub(true);
    } catch (e) { toast.error('Failed to load'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === carriers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(carriers.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected carrier(s)? This cannot be undone.`)) return;
    setDeleting(true);
    let deleted = 0;
    for (const id of selected) {
      try {
        await axios.delete(`${API}/carriers/${id}`);
        deleted++;
      } catch (e) { /* continue */ }
    }
    toast.success(`Deleted ${deleted} carrier(s)`);
    setSelected(new Set());
    setDeleting(false);
    fetchCarriers();
    fetchStats();
  };

  const regionMap = {};
  regions.forEach(r => { regionMap[r.id] = r; });

  return (
    <div className="space-y-6" data-testid="carriers-tab">
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-zinc-900">{stats.total_active}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Active</div>
          </div>
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-blue-600">{stats.otr_count}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">OTR</div>
          </div>
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-orange-600">{stats.drayage_count}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Drayage</div>
          </div>
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-green-600">{stats.nationwide_count}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Nationwide</div>
          </div>
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-red-600">{stats.unsubscribed_count}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Unsubscribed</div>
          </div>
        </div>
      )}

      {/* Actions Bar (Admin Only) */}
      {isAdmin && (
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 font-bold text-sm uppercase tracking-wide" data-testid="add-carrier-btn">
            <Plus className="w-4 h-4" /> Add Carrier
          </button>
          <button onClick={() => setShowBulkModal(true)} className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 font-bold text-sm uppercase tracking-wide" data-testid="bulk-import-btn">
            <Upload className="w-4 h-4" /> Bulk Import
          </button>
          <button onClick={() => setShowCsvModal(true)} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 font-bold text-sm uppercase tracking-wide" data-testid="csv-import-btn">
            <FileSpreadsheet className="w-4 h-4" /> CSV Import
          </button>
          <button onClick={() => setShowRegionModal(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-bold text-sm uppercase tracking-wide" data-testid="manage-regions-btn">
            <MapPin className="w-4 h-4" /> Manage Regions
          </button>
          <button onClick={loadUnsubscribed} className="flex items-center gap-2 border border-zinc-300 text-zinc-700 hover:border-zinc-400 px-4 py-2 font-bold text-sm uppercase tracking-wide" data-testid="view-unsubscribed-btn">
            <Mail className="w-4 h-4" /> Unsubscribed
          </button>
          {selected.size > 0 && (
            <button onClick={handleBulkDelete} disabled={deleting}
              className="flex items-center gap-2 bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 font-bold text-sm uppercase tracking-wide disabled:opacity-50"
              data-testid="bulk-delete-btn">
              <Trash2 className="w-4 h-4" /> Delete {selected.size} Selected
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input type="text" value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            placeholder="Search email or company..." className="w-full pl-10 pr-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm" data-testid="carrier-search" />
        </div>
        <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v, region_id: '' })}>
          <SelectTrigger className="h-10 border-zinc-300" data-testid="filter-carrier-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="OTR">OTR Only</SelectItem>
            <SelectItem value="DRAYAGE">Drayage Only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.region_id || 'all'} onValueChange={(v) => setFilter({ ...filter, region_id: v === 'all' ? '' : v })}>
          <SelectTrigger className="h-10 border-zinc-300" data-testid="filter-region">
            <SelectValue placeholder="All Regions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {regions.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.region_type})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.is_active} onValueChange={(v) => setFilter({ ...filter, is_active: v })}>
          <SelectTrigger className="h-10 border-zinc-300" data-testid="filter-active">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Carrier Table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-100 flex justify-between items-center">
          <span className="text-sm text-zinc-500">{total} carrier(s) found</span>
        </div>
        {loading ? (
          <div className="text-center py-12 text-zinc-400">Loading...</div>
        ) : carriers.length === 0 ? (
          <div className="text-center py-12 text-zinc-400">No carriers found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="carriers-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  {isAdmin && (
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" checked={carriers.length > 0 && selected.size === carriers.length}
                        onChange={toggleSelectAll} className="rounded border-zinc-300" data-testid="select-all-carriers" />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Regions</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Status</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {carriers.map(c => (
                  <tr key={c.id} className={`hover:bg-zinc-50 ${selected.has(c.id) ? 'bg-red-50' : ''}`}>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.has(c.id)}
                          onChange={() => toggleSelect(c.id)} className="rounded border-zinc-300" data-testid={`select-carrier-${c.id}`} />
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">{c.email}</td>
                    <td className="px-4 py-3 text-sm text-zinc-600">{c.company_name || '\u2014'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase ${TYPE_COLORS[c.carrier_type] || 'bg-zinc-100 text-zinc-700'}`}>
                        {c.carrier_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {(c.region_ids || []).length === 0 ? (
                        <span className="flex items-center gap-1 text-green-600 text-xs font-bold"><Globe className="w-3 h-3" /> Nationwide</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(c.region_ids || []).slice(0, 3).map(rid => {
                            const reg = regionMap[rid];
                            return reg ? (
                              <span key={rid} className="px-1.5 py-0.5 text-xs bg-zinc-100 text-zinc-600">{reg.name}</span>
                            ) : null;
                          })}
                          {(c.region_ids || []).length > 3 && (
                            <span className="px-1.5 py-0.5 text-xs bg-zinc-100 text-zinc-500">+{c.region_ids.length - 3}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-500'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleToggle(c)} className="p-1.5 text-zinc-400 hover:text-zinc-900" title={c.is_active ? 'Deactivate' : 'Activate'}>
                            {c.is_active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5" />}
                          </button>
                          <button onClick={() => handleDelete(c.id)} className="p-1.5 text-zinc-400 hover:text-red-600" data-testid={`delete-carrier-${c.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && <AddCarrierModal regions={regions} onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchCarriers(); fetchStats(); }} />}
      {showBulkModal && <BulkImportModal regions={regions} onClose={() => setShowBulkModal(false)} onSuccess={() => { setShowBulkModal(false); fetchCarriers(); fetchStats(); }} />}
      {showCsvModal && <CsvImportModal regions={regions} onClose={() => setShowCsvModal(false)} onSuccess={() => { setShowCsvModal(false); fetchCarriers(); fetchStats(); }} />}
      {showRegionModal && <RegionManageModal regions={regions} onClose={() => { setShowRegionModal(false); fetchRegions(); fetchStats(); }} />}
      {showUnsub && <UnsubscribedModal data={unsubscribed} onClose={() => setShowUnsub(false)} />}
    </div>
  );
};


// ==================== CARRIER TYPE + REGION SELECTOR (shared) ====================
const TypeRegionSelector = ({ carrierType, setCarrierType, regionIds, setRegionIds, regions }) => {
  const filteredRegions = regions.filter(r =>
    carrierType === 'OTR' ? r.region_type === 'STATE' : (r.region_type === 'PORT' || r.region_type === 'RAIL')
  );

  const toggleRegion = (id) => {
    setRegionIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Carrier Type *</label>
        <p className="text-xs text-zinc-400 mb-3">Choose one: OTR carriers handle long-haul interstate loads. Drayage carriers handle port/rail container moves.</p>
        <div className="flex gap-3">
          <button type="button" onClick={() => { setCarrierType('OTR'); setRegionIds([]); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 font-bold text-sm uppercase border-2 transition-colors ${carrierType === 'OTR' ? 'bg-blue-600 text-white border-blue-600' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'}`}
            data-testid="select-type-otr">
            <TruckIcon className="w-4 h-4" /> OTR
          </button>
          <button type="button" onClick={() => { setCarrierType('DRAYAGE'); setRegionIds([]); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 font-bold text-sm uppercase border-2 transition-colors ${carrierType === 'DRAYAGE' ? 'bg-orange-600 text-white border-orange-600' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'}`}
            data-testid="select-type-drayage">
            <Anchor className="w-4 h-4" /> Drayage
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
          {carrierType === 'OTR' ? 'Operating States' : 'Ports / Rail Ramps'}
          <span className="text-zinc-400 normal-case ml-1">(optional)</span>
        </label>
        <p className="text-xs text-zinc-400 mb-3">
          {carrierType === 'OTR'
            ? 'Select states where this carrier operates. Leave empty = receives ALL nationwide OTR loads.'
            : 'Select ports/ramps this carrier serves. Leave empty = receives ALL drayage loads nationwide.'}
        </p>
        {filteredRegions.length === 0 ? (
          <p className="text-sm text-zinc-400 italic">No {carrierType === 'OTR' ? 'states' : 'ports/ramps'} configured yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
            {filteredRegions.map(r => {
              const Icon = REGION_ICONS[r.region_type] || MapPin;
              return (
                <button key={r.id} type="button" onClick={() => toggleRegion(r.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold border transition-colors ${
                    regionIds.includes(r.id)
                      ? (carrierType === 'OTR' ? 'bg-blue-600 text-white border-blue-600' : 'bg-orange-600 text-white border-orange-600')
                      : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                  }`}>
                  <Icon className="w-3 h-3" /> {r.name}
                </button>
              );
            })}
          </div>
        )}
        {regionIds.length === 0 && (
          <div className="flex items-center gap-1 mt-2 text-xs text-green-600 font-medium">
            <Globe className="w-3 h-3" /> Will receive all {carrierType} loads nationwide
          </div>
        )}
      </div>
    </>
  );
};


// ==================== ADD CARRIER MODAL ====================
const AddCarrierModal = ({ regions, onClose, onSuccess }) => {
  const [form, setForm] = useState({ email: '', company_name: '', phone: '', mc_number: '' });
  const [carrierType, setCarrierType] = useState('OTR');
  const [regionIds, setRegionIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/carriers`, { ...form, carrier_type: carrierType, region_ids: regionIds });
      toast.success('Carrier added');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add carrier');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <h2 className="text-xl font-black text-zinc-900">Add Carrier</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Email *</label>
            <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm" data-testid="add-carrier-email" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Company Name</label>
            <input type="text" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="w-full px-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Phone</label>
              <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">MC Number</label>
              <input type="text" value={form.mc_number} onChange={e => setForm({...form, mc_number: e.target.value})} className="w-full px-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm" />
            </div>
          </div>
          <TypeRegionSelector carrierType={carrierType} setCarrierType={setCarrierType} regionIds={regionIds} setRegionIds={setRegionIds} regions={regions} />
          <button type="submit" disabled={submitting} className="w-full bg-red-600 hover:bg-red-700 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50" data-testid="save-carrier-btn">
            {submitting ? 'Adding...' : 'Add Carrier'}
          </button>
        </form>
      </div>
    </div>
  );
};


// ==================== BULK IMPORT MODAL ====================
const BulkImportModal = ({ regions, onClose, onSuccess }) => {
  const [emailsText, setEmailsText] = useState('');
  const [carrierType, setCarrierType] = useState('OTR');
  const [regionIds, setRegionIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emails = emailsText.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    if (!emails.length) { toast.error('Enter at least one email'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/carriers/bulk`, { emails, carrier_type: carrierType, region_ids: regionIds });
      toast.success(`Added ${res.data.added} carriers (${res.data.skipped} skipped)`);
      onSuccess();
    } catch (e) { toast.error('Bulk import failed'); } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <h2 className="text-xl font-black text-zinc-900">Bulk Import Carriers</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Emails (one per line, or comma-separated)</label>
            <textarea value={emailsText} onChange={e => setEmailsText(e.target.value)} rows={6} className="w-full px-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm font-mono resize-none" placeholder="carrier1@example.com&#10;carrier2@example.com" data-testid="bulk-emails-input" />
          </div>
          <TypeRegionSelector carrierType={carrierType} setCarrierType={setCarrierType} regionIds={regionIds} setRegionIds={setRegionIds} regions={regions} />
          <button type="submit" disabled={submitting} className="w-full bg-red-600 hover:bg-red-700 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50" data-testid="bulk-import-submit">
            {submitting ? 'Importing...' : 'Import Carriers'}
          </button>
        </form>
      </div>
    </div>
  );
};


// ==================== CSV IMPORT MODAL ====================
const CsvImportModal = ({ regions, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [carrierType, setCarrierType] = useState('OTR');
  const [regionIds, setRegionIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please select a CSV file'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('carrier_type', carrierType);
      formData.append('region_ids', regionIds.join(','));
      const res = await axios.post(`${API}/carriers/csv-import`, formData,  { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      toast.success(`Imported ${res.data.added} carriers`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV import failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <h2 className="text-xl font-black text-zinc-900">Import from CSV</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>
        {result ? (
          <div className="p-6 space-y-4">
            <div className="text-center py-4">
              <div className="text-4xl font-black text-green-600 mb-2">{result.added}</div>
              <div className="text-sm text-zinc-500 uppercase font-bold">Carriers Imported</div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-zinc-50 p-3 border border-zinc-200"><div className="text-lg font-bold text-zinc-900">{result.total_rows}</div><div className="text-xs text-zinc-500">Total Rows</div></div>
              <div className="bg-zinc-50 p-3 border border-zinc-200"><div className="text-lg font-bold text-amber-600">{result.skipped}</div><div className="text-xs text-zinc-500">Skipped</div></div>
              <div className="bg-zinc-50 p-3 border border-zinc-200"><div className="text-lg font-bold text-red-600">{result.errors}</div><div className="text-xs text-zinc-500">Errors</div></div>
            </div>
            <button onClick={onSuccess} className="w-full bg-zinc-900 text-white py-3 font-bold uppercase text-sm">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">CSV File *</label>
              <div className="border-2 border-dashed border-zinc-300 p-6 text-center hover:border-zinc-400 transition-colors">
                <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="csv-file-input" data-testid="csv-file-input" />
                <label htmlFor="csv-file-input" className="cursor-pointer">
                  <FileSpreadsheet className="w-10 h-10 text-zinc-400 mx-auto mb-2" />
                  {file ? <p className="text-sm font-medium text-zinc-900">{file.name}</p> : <p className="text-sm text-zinc-500">Click to select a CSV file</p>}
                </label>
              </div>
              <p className="text-xs text-zinc-400 mt-2">CSV must have an <strong>email</strong> column. Optional: company_name, phone, mc_number</p>
            </div>
            <TypeRegionSelector carrierType={carrierType} setCarrierType={setCarrierType} regionIds={regionIds} setRegionIds={setRegionIds} regions={regions} />
            <button type="submit" disabled={submitting || !file} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50" data-testid="csv-import-submit">
              {submitting ? 'Importing...' : 'Import Carriers from CSV'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};


// ==================== REGION MANAGEMENT MODAL ====================
const RegionManageModal = ({ regions: initialRegions, onClose }) => {
  const [regions, setRegions] = useState(initialRegions);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('STATE');
  const [adding, setAdding] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/regions`, { name: newName.trim(), region_type: newType });
      toast.success('Region added');
      setNewName('');
      const res = await axios.get(`${API}/regions`);
      setRegions(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add region'); } finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this region? Carriers assigned to it will lose this region.')) return;
    try {
      await axios.delete(`${API}/regions/${id}`);
      setRegions(prev => prev.filter(r => r.id !== id));
      toast.success('Region deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const filtered = filterType === 'all' ? regions : regions.filter(r => r.region_type === filterType);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <h2 className="text-xl font-black text-zinc-900">Manage Regions</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <form onSubmit={handleAdd} className="space-y-2">
            <div className="flex gap-2">
              <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Region name (e.g., Texas)" className="flex-1 px-4 py-2.5 border border-zinc-300 focus:border-zinc-900 outline-none text-sm" data-testid="new-region-input" />
              <select value={newType} onChange={e => setNewType(e.target.value)} className="px-3 py-2.5 border border-zinc-300 text-sm bg-white">
                <option value="STATE">State</option>
                <option value="PORT">Port</option>
                <option value="RAIL">Rail</option>
              </select>
              <button type="submit" disabled={adding} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 font-bold text-sm uppercase disabled:opacity-50" data-testid="add-region-btn">Add</button>
            </div>
          </form>
          <div className="flex gap-2">
            {['all', 'STATE', 'PORT', 'RAIL'].map(t => (
              <button key={t} onClick={() => setFilterType(t)}
                className={`px-3 py-1.5 text-xs font-bold uppercase border ${filterType === t ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-600'}`}>
                {t === 'all' ? 'All' : t}
              </button>
            ))}
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {filtered.map(r => {
              const Icon = REGION_ICONS[r.region_type] || MapPin;
              return (
                <div key={r.id} className="flex justify-between items-center bg-zinc-50 px-4 py-2.5 border border-zinc-200">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-zinc-400" />
                    <div>
                      <div className="font-medium text-sm text-zinc-900">{r.name}</div>
                      <div className="text-xs text-zinc-400">{r.region_type}</div>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(r.id)} className="p-1 text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
            {filtered.length === 0 && <p className="text-sm text-zinc-400 text-center py-4">No regions found</p>}
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== UNSUBSCRIBED MODAL ====================
const UnsubscribedModal = ({ data, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white w-full max-w-lg max-h-[80vh] overflow-y-auto">
      <div className="flex justify-between items-center p-6 border-b border-zinc-200">
        <h2 className="text-xl font-black text-zinc-900">Unsubscribed Carriers ({data.length})</h2>
        <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6">
        {data.length === 0 ? (
          <p className="text-zinc-400 text-center py-4">No unsubscribed carriers</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-2 text-xs font-bold uppercase text-zinc-500">Email</th><th className="text-left py-2 text-xs font-bold uppercase text-zinc-500">Date</th></tr></thead>
            <tbody>
              {data.map(u => (
                <tr key={u.id} className="border-b border-zinc-100">
                  <td className="py-2 text-zinc-700">{u.email}</td>
                  <td className="py-2 text-zinc-500">{new Date(u.unsubscribed_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);
